// redux/slices/anamnesisSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { resetStore } from './resetStore';
import { getServerUrl, getAuthHeaders } from '../../Utils/serverSelector';

// ----- helpers -----

// снимаем Content-Type, чтобы axios сам поставил boundary для multipart/form-data
const stripContentType = (h = {}) => {
    const copy = { ...h };
    delete copy['Content-Type'];
    delete copy['content-type'];

    // если вдруг там есть common/post (axios defaults) — на всякий
    if (copy.common) {
        delete copy.common['Content-Type'];
        delete copy.common['content-type'];
    }
    if (copy.post) {
        delete copy.post['Content-Type'];
        delete copy.post['content-type'];
    }

    return copy;
};

// headers для multipart + JWT
const buildMultipartHeaders = async () => {
    const authHeaders = await getAuthHeaders();
    return stripContentType(authHeaders);
};

// Универсальное добавление файла в FormData (Web File/Blob, RN {uri,...}, строка-URL)
const appendMaybeFile = (fd, field, val, fallbackName) => {
    if (!val) return;

    // Web: File
    if (typeof File !== 'undefined' && val instanceof File) {
        fd.append(field, val, val.name || fallbackName);
        return;
    }
    // Web: Blob (без имени)
    if (typeof Blob !== 'undefined' && val instanceof Blob) {
        fd.append(field, val, fallbackName);
        return;
    }
    // React Native объект
    if (val?.uri) {
        fd.append(field, {
            uri: val.uri,
            name: val.fileName || fallbackName,
            type: val.mimeType || 'image/jpeg',
        });
        return;
    }
    // На крайний случай — строка
    if (typeof val === 'string') {
        fd.append(field, val);
    }
};

// ----- Thunks -----

export const addAnamnesis = createAsyncThunk(
    'anamnesis/addAnamnesis',
    async ({ uid, anamnesis }, { rejectWithValue }) => {
        try {
            if (!uid) throw new Error('UID не передан');
            if (!anamnesis?.clientId) throw new Error('clientId не передан');

            const formData = new FormData();
            formData.append('uid', uid);
            formData.append('clientId', String(anamnesis.clientId));
            formData.append('medicalNote', anamnesis.medicalNote || '');

            appendMaybeFile(formData, 'photoBefore', anamnesis.photoBefore, `photoBefore-${Date.now()}.jpg`);
            appendMaybeFile(formData, 'photoAfter', anamnesis.photoAfter, `photoAfter-${Date.now()}.jpg`);

            const headers = await buildMultipartHeaders();

            const res = await axios.post(`${getServerUrl()}/anamnesis`, formData, {
                headers,
                withCredentials: true,
                validateStatus: () => true,
                transformRequest: [
                    (data, h) => {
                        // жёстко снимаем любые Content-Type, которые могли прописаться инстансом axios
                        if (h) {
                            if (h.common) {
                                delete h.common['Content-Type'];
                                delete h.common['content-type'];
                            }
                            if (h.post) {
                                delete h.post['Content-Type'];
                                delete h.post['content-type'];
                            }
                            delete h['Content-Type'];
                            delete h['content-type'];
                        }
                        return data;
                    },
                ],
            });

            if (res.status !== 201 && res.status !== 200) {
                throw new Error(res.data?.message || `Ошибка сервера: ${res.status}`);
            }

            return res.data; // сервер возвращает сам объект анамнеза
        } catch (e) {
            return rejectWithValue(e.response?.data || e.message || 'Ошибка при создании анамнеза');
        }
    }
);

export const fetchAnamnesisByClient = createAsyncThunk(
    'anamnesis/fetchByClient',
    async ({ uid, clientId }, { rejectWithValue }) => {
        try {
            if (!uid) throw new Error('UID не передан');
            if (!clientId) throw new Error('clientId не передан');

            const authHeaders = await getAuthHeaders();

            const res = await axios.get(
                `${getServerUrl()}/anamnesis/client/${encodeURIComponent(clientId)}`,
                {
                    headers: authHeaders,
                    params: { uid },
                    withCredentials: true,
                    validateStatus: () => true,
                }
            );

            if (res.status !== 200) {
                throw new Error(
                    res.data?.message ||
                    res.data?.error ||
                    `Ошибка сервера: ${res.status}`
                );
            }

            const data = res.data;

            if (Array.isArray(data)) return data;
            if (Array.isArray(data?.anamneses)) return data.anamneses;
            if (Array.isArray(data?.data)) return data.data;

            return [];
        } catch (e) {
            return rejectWithValue(
                e.response?.data?.message ||
                e.response?.data?.error ||
                e.message ||
                'Ошибка загрузки анамнеза'
            );
        }
    }
);

export const updateAnamnesis = createAsyncThunk(
    'anamnesis/updateAnamnesis',
    async ({ uid, id, updates }, { rejectWithValue }) => {
        try {
            if (!uid) throw new Error('UID не передан');
            if (!id) throw new Error('ID не передан');

            const { clientId, medicalNote, photoBefore, photoAfter } = updates || {};

            const formData = new FormData();
            formData.append('uid', uid);
            formData.append('id', String(id));
            if (clientId) formData.append('clientId', String(clientId));
            formData.append('medicalNote', medicalNote || '');

            appendMaybeFile(formData, 'photoBefore', photoBefore, `photoBefore-${Date.now()}.jpg`);
            appendMaybeFile(formData, 'photoAfter', photoAfter, `photoAfter-${Date.now()}.jpg`);

            const headers = await buildMultipartHeaders();

            const res = await axios.put(`${getServerUrl()}/anamnesis/${id}`, formData, {
                headers,
                withCredentials: true,
                validateStatus: () => true,
                transformRequest: [
                    (data, h) => {
                        if (h) {
                            if (h.common) {
                                delete h.common['Content-Type'];
                                delete h.common['content-type'];
                            }
                            if (h.post) {
                                delete h.post['Content-Type'];
                                delete h.post['content-type'];
                            }
                            delete h['Content-Type'];
                            delete h['content-type'];
                        }
                        return data;
                    },
                ],
            });

            if (res.status !== 200) {
                throw new Error(res.data?.message || `Ошибка сервера: ${res.status}`);
            }

            return res.data;
        } catch (e) {
            return rejectWithValue(e.response?.data || e.message || 'Ошибка при обновлении анамнеза');
        }
    }
);

export const deleteAnamnesis = createAsyncThunk(
    'anamnesis/deleteAnamnesis',
    async ({ id, uid, clientId }, { rejectWithValue }) => {
        try {
            if (!id) throw new Error('ID не передан');
            if (!uid) throw new Error('UID не передан');

            const authHeaders = await getAuthHeaders();

            const res = await axios.delete(
                `${getServerUrl()}/anamnesis/${encodeURIComponent(id)}`,
                {
                    headers: authHeaders,
                    params: {
                        uid,
                        ...(clientId ? { clientId } : {}),
                    },
                    data: {
                        uid,
                        ...(clientId ? { clientId } : {}),
                    },
                    withCredentials: true,
                    validateStatus: () => true,
                }
            );

            if (res.status !== 200 && res.status !== 204) {
                throw new Error(
                    res.data?.message ||
                    res.data?.error ||
                    `Ошибка сервера: ${res.status}`
                );
            }

            return id;
        } catch (e) {
            return rejectWithValue(
                e.response?.data?.message ||
                e.response?.data?.error ||
                e.message ||
                'Ошибка при удалении анамнеза'
            );
        }
    }
);

// ----- Slice -----

const initialState = {
    anamneses: [],
    activeClientId: null,
    status: 'idle',
    error: null,
};

const anamnesisSlice = createSlice({
    name: 'anamnesis',
    initialState,
    reducers: {
        setAnamneses: (state, action) => {
            state.anamneses = Array.isArray(action.payload) ? action.payload : [];
        },
    },
    extraReducers: (builder) => {
        builder
            // add
            .addCase(addAnamnesis.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(addAnamnesis.fulfilled, (state, action) => {
                state.status = 'succeeded';

                const created = action.payload;
                const createdClientId =
                    created?.clientId ??
                    created?.client_id ??
                    created?.ClientId ??
                    created?.client?.id;

                if (
                    created &&
                    (
                        state.activeClientId == null ||
                        createdClientId == null ||
                        String(createdClientId) === String(state.activeClientId)
                    )
                ) {
                    state.anamneses.unshift(created);
                }
            })
            .addCase(addAnamnesis.rejected, (state, action) => {
                state.status = 'failed';
                state.error =
                    action.payload || action.error?.message || 'Ошибка при создании анамнеза';
            })

            // fetch
            .addCase(fetchAnamnesisByClient.pending, (state, action) => {
                state.status = 'loading';
                state.error = null;

                const requestedClientId = action.meta?.arg?.clientId;

                // При открытии другого клиента сразу очищаем старый прогресс,
                // чтобы записи предыдущего клиента не мигали на экране.
                if (
                    requestedClientId != null &&
                    String(state.activeClientId ?? '') !== String(requestedClientId)
                ) {
                    state.anamneses = [];
                }

                state.activeClientId = requestedClientId ?? null;
            })
            .addCase(fetchAnamnesisByClient.fulfilled, (state, action) => {
                const requestedClientId = action.meta?.arg?.clientId;

                // Не записываем запоздалый ответ от ранее открытого клиента.
                if (
                    state.activeClientId != null &&
                    requestedClientId != null &&
                    String(state.activeClientId) !== String(requestedClientId)
                ) {
                    return;
                }

                state.status = 'succeeded';
                state.error = null;
                state.anamneses = Array.isArray(action.payload) ? action.payload : [];
                state.activeClientId = requestedClientId ?? null;
            })
            .addCase(fetchAnamnesisByClient.rejected, (state, action) => {
                const requestedClientId = action.meta?.arg?.clientId;

                // Игнорируем ошибку устаревшего запроса.
                if (
                    state.activeClientId != null &&
                    requestedClientId != null &&
                    String(state.activeClientId) !== String(requestedClientId)
                ) {
                    return;
                }

                state.status = 'failed';
                state.anamneses = [];
                state.error =
                    action.payload || action.error?.message || 'Ошибка загрузки анамнеза';
            })

            // update
            .addCase(updateAnamnesis.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(updateAnamnesis.fulfilled, (state, action) => {
                state.status = 'succeeded';
                const updated = action.payload;
                if (!updated?.id) return;
                const idx = state.anamneses.findIndex(
                    (a) => String(a.id) === String(updated.id)
                );
                if (idx !== -1) {
                    state.anamneses[idx] = { ...state.anamneses[idx], ...updated };
                } else {
                    state.anamneses.unshift(updated);
                }
            })
            .addCase(updateAnamnesis.rejected, (state, action) => {
                state.status = 'failed';
                state.error =
                    action.payload || action.error?.message || 'Ошибка при обновлении анамнеза';
            })

            // delete
            .addCase(deleteAnamnesis.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(deleteAnamnesis.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.anamneses = state.anamneses.filter(
                    (a) => String(a.id) !== String(action.payload)
                );
            })
            .addCase(deleteAnamnesis.rejected, (state, action) => {
                state.status = 'failed';
                state.error =
                    action.payload || action.error?.message || 'Ошибка при удалении анамнеза';
            })

            .addCase(resetStore, () => initialState);
    },
});

export const { setAnamneses } = anamnesisSlice.actions;
export default anamnesisSlice.reducer;
